/* ioc-ui.js · Inside Out Cookie global UI system (A1, CRO rebuild 2026-09-23). Loaded deferred, after every other
   ioc script. No libraries. Each part is independent and safe to run on any template:

     1. header      sticky group compresses on scroll down past 120px, returns on scroll up (transforms only)
     2. ticker      phones: facts crossfade every 5s; paused by reduced motion / pause-motion; tap = next + stop
     3. SHEET       window.IOCSheet.open(id, opener) / .close(id) / .isOpen(id); [data-ioc-sheet-open="id"] triggers
     4. desktop nav "Shop cookies" / "Group orders" panels (click or hover, Escape, outside click)
     5. footer      accordions on phones, one open at a time
     6. RAIL        counter, dots (<= 5 cards), desktop arrows with disabled ends, "i of n" slide labels
     7. TABS        roving tabindex, arrow keys, URL hash sync (#packs)
     8. ACTION-BAR  watches [data-ioc-primary-cta] (or the mode's fallback), card modes, product-form proxy
     9. state       html.ioc-typing (a text field has focus), html.ioc-dialog-open (a Horizon dialog is open)

   Nothing here fakes urgency or invents a number: every price the bar prints was rendered by Liquid. */
(function () {
  "use strict";
  var root = document.documentElement;
  var RM = window.matchMedia ? window.matchMedia("(prefers-reduced-motion: reduce)") : { matches: false };
  function ready(fn) { if (document.readyState === "loading") document.addEventListener("DOMContentLoaded", fn); else fn(); }
  function reduced() { return RM.matches || root.classList.contains("ioc-motion-paused"); }
  function isPhone() { return window.innerWidth < 900; }
  function $$(sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); }
  function raf(fn) { var q = false; return function () { if (q) return; q = true; requestAnimationFrame(function () { q = false; fn(); }); }; }
  // Horizon scrolls .page-wrapper (not the window) at >= 990px, and the window below that. Read whichever is live.
  var PW = null;
  function pageScroller() {
    if (!PW || !PW.isConnected) PW = document.querySelector(".page-wrapper");
    if (PW && PW.scrollHeight > PW.clientHeight + 1 && /(auto|scroll)/.test(getComputedStyle(PW).overflowY)) return PW;
    return null;
  }
  function scrollTop() { var pw = pageScroller(); return pw ? pw.scrollTop : (window.scrollY || document.documentElement.scrollTop || 0); }
  // one listener for both: scroll does not bubble, but a capture listener on document sees the wrapper's scroll
  function onPageScroll(fn) {
    document.addEventListener("scroll", function (e) {
      var t = e.target;
      if (t === document || t === document.documentElement || t === document.body || (t && t.classList && t.classList.contains("page-wrapper"))) fn();
    }, { capture: true, passive: true });
  }

  /* ================================================================ 1. header ===== */
  var hdr = { rest: 106, compact: 56, on: false };
  function initHeader() {
    var group = document.getElementById("header-group");
    if (!group) return;
    var tick = group.querySelector(".ioc-ticker-section");
    var nav = group.querySelector(".ioc-nav-section");
    function measure() {
      var tickH = tick ? tick.offsetHeight : 0;
      var navH = nav ? nav.offsetHeight : 0;
      hdr.compact = Math.min(navH, isPhone() ? 56 : 60);
      hdr.rest = tickH + navH;
      root.style.setProperty("--ioc-hdr-shift", (tickH + navH - hdr.compact) + "px");
      publish();
    }
    function publish() { root.style.setProperty("--ioc-hdr-visible", (hdr.on ? hdr.compact : hdr.rest) + "px"); }
    function set(c) {
      if (c === hdr.on) return;
      hdr.on = c;
      root.classList.toggle("ioc-hdr-compact", c);
      publish();
    }
    var lastY = scrollTop();
    onPageScroll(raf(function () {
      var y = Math.max(0, scrollTop());
      if (root.classList.contains("ioc-scroll-lock")) { lastY = y; return; }
      if (y <= 120) set(false);
      else if (y > lastY + 4) set(true);
      else if (y < lastY - 4) set(false);
      lastY = y;
    }));
    window.addEventListener("resize", raf(measure));
    // keyboard focus landing in the hidden part of the header (Shift+Tab back up) shows it in full;
    // focus on a visible item (menu, cart) leaves it alone so the phone snap doesn't jump
    group.addEventListener("focusin", function (e) { if (e.target.getBoundingClientRect().top < 0) set(false); });
    // final-a: Shift+Tab from the nav's first stop goes to the ticker's pause button, and Chromium scrolls the page
    // up to reach it (380px on a phone while the header is slid up, 460px on desktop even with it in view). Show the
    // header first and focus the button without that scroll; every other Tab keeps the browser's own order.
    group.addEventListener("keydown", function (e) {
      if (e.key !== "Tab" || !e.shiftKey || !nav || !tick) return;
      var toggle = tick.querySelector(".motion-toggle");
      if (!toggle || !toggle.getClientRects().length) return;
      var list = nav.querySelectorAll("a[href], button:not([disabled]), input:not([disabled]), [tabindex]:not([tabindex='-1'])");
      var first = null;
      for (var i = 0; i < list.length; i++) { if (list[i].getClientRects().length) { first = list[i]; break; } }
      if (e.target !== first) return;
      e.preventDefault();
      set(false);
      toggle.focus({ preventScroll: true });
    });
    measure();
    window.addEventListener("load", measure);
  }

  /* ================================================================ 2. ticker ===== */
  function initTicker() {
    $$("[data-ioc-tick]").forEach(function (t) {
      if (t.dataset.iocReady) return;
      t.dataset.iocReady = "1";
      var items = $$(".ioc-tick__item", t);
      if (items.length < 2) return;
      var i = 0, stopped = false;
      function show(n) {
        items[i].classList.remove("is-on");
        i = (n + items.length) % items.length;
        items[i].classList.add("is-on");
      }
      setInterval(function () {
        if (stopped || reduced() || document.hidden || !isPhone()) return;
        show(i + 1);
      }, 5000);
      t.addEventListener("click", function (e) {
        if (e.target.closest(".motion-toggle") || !isPhone()) return;
        stopped = true;
        show(i + 1);
      });
    });
  }

  /* ================================================================ 3. SHEET ===== */
  /* Opens as a NON-modal top-layer popover (paints above everything, stays in place in the DOM) and makes the rest
     of the page inert by hand, SKIPPING Shopify's consent banner, so a shopper can always reach Accept / Decline
     (see the cookie-banner guard in ioc-cro.js). Falls back to showModal() where the Popover API is missing. */
  var HAS_POPOVER = typeof HTMLElement !== "undefined" && Object.prototype.hasOwnProperty.call(HTMLElement.prototype, "popover");
  var openSheets = [];
  var FOCUSABLE = "button:not([disabled]), [href], input:not([disabled]):not([type='hidden']), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex='-1'])";

  function isConsentNode(el) {
    if (!el || el.nodeType !== 1) return false;
    if (/^shopify-pc/.test(el.id || "") || el.id === "ioc-cc") return true;
    if (el.classList && (el.classList.contains("shopify-pc__banner") || el.classList.contains("ioc-cc"))) return true;
    try { return !!el.querySelector("#shopify-pc__banner, .shopify-pc__banner__dialog, [id^='shopify-pc__'], #ioc-cc"); } catch (e) { return false; }
  }
  function isDialogShell(el) {
    // A closed Horizon dialog (cart drawer, search, capture modal) is not rendered, so leaving it un-inert costs
    // nothing, and inerting it would break it if it opens while a sheet is up (the cart drawer after an add).
    if (el.tagName === "DIALOG") return true;
    for (var c = el.firstElementChild; c; c = c.nextElementSibling) if (c.tagName === "DIALOG") return true;
    return false;
  }
  function inertOthers(dlg) {
    var list = [], node = dlg;
    while (node && node.parentElement && node !== document.body) {
      var p = node.parentElement;
      for (var k = 0; k < p.children.length; k++) {
        var sib = p.children[k];
        if (sib === node || sib.inert || /^(SCRIPT|STYLE|LINK|TEMPLATE|NOSCRIPT)$/.test(sib.tagName)) continue;
        if (isConsentNode(sib) || isDialogShell(sib)) continue;
        sib.inert = true;
        list.push(sib);
      }
      node = p;
    }
    dlg.__iocInert = list;
  }
  function releaseInert(dlg) {
    (dlg.__iocInert || []).forEach(function (el) { el.inert = false; });
    dlg.__iocInert = null;
  }
  function lockScroll() {
    if (root.classList.contains("ioc-scroll-lock")) return;
    root.style.setProperty("--ioc-sbw", Math.max(0, window.innerWidth - root.clientWidth) + "px");
    root.classList.add("ioc-scroll-lock");
  }
  function unlockScroll() { root.classList.remove("ioc-scroll-lock"); root.style.removeProperty("--ioc-sbw"); }
  function syncOpeners(dlg, open) {
    $$("[data-ioc-sheet-open]").forEach(function (o) {
      if (o.getAttribute("data-ioc-sheet-open") === dlg.id) o.setAttribute("aria-expanded", open ? "true" : "false");
    });
  }
  function sheetById(id) {
    var d = typeof id === "string" ? document.getElementById(id) : id;
    return d && d.matches && d.matches("[data-ioc-sheet]") ? d : null;
  }
  function openSheet(id, opener) {
    var dlg = sheetById(id);
    if (!dlg || dlg.open) return false;
    dlg.__iocOpener = opener || document.activeElement;
    var pop = false;
    if (HAS_POPOVER && typeof dlg.showPopover === "function") {
      try {
        if (!dlg.hasAttribute("popover")) dlg.setAttribute("popover", "manual");
        dlg.showPopover();
        pop = true;
      } catch (e) { pop = false; dlg.removeAttribute("popover"); }
    }
    if (pop) {
      try { dlg.show(); } catch (e) {}
      inertOthers(dlg);
      dlg.setAttribute("aria-modal", "true"); // the page behind is inert, so assistive tech is told it is modal
    } else if (typeof dlg.showModal === "function") {
      dlg.showModal();
    } else {
      dlg.setAttribute("open", "");
    }
    dlg.__iocPop = pop;
    openSheets.push(dlg);
    lockScroll();
    root.classList.add("ioc-sheet-open");
    syncOpeners(dlg, true);
    var first = dlg.querySelector("[autofocus]") || dlg.querySelector(".ioc-sheet__close") || dlg.querySelector(FOCUSABLE);
    if (first) { try { first.focus({ preventScroll: true }); } catch (e) { first.focus(); } }
    dlg.dispatchEvent(new CustomEvent("ioc:sheet-open", { bubbles: true, detail: { id: dlg.id } }));
    return true;
  }
  function cleanupSheet(dlg, restoreFocus) {
    if (dlg.__iocPop) {
      try { if (dlg.matches(":popover-open")) dlg.hidePopover(); } catch (e) {}
      releaseInert(dlg);
      dlg.removeAttribute("aria-modal");
    }
    dlg.__iocPop = false;
    openSheets = openSheets.filter(function (d) { return d !== dlg; });
    if (!openSheets.length) { unlockScroll(); root.classList.remove("ioc-sheet-open"); }
    syncOpeners(dlg, false);
    var o = dlg.__iocOpener;
    dlg.__iocOpener = null;
    if (restoreFocus !== false && o && document.contains(o) && typeof o.focus === "function") {
      try { o.focus({ preventScroll: true }); } catch (e) { o.focus(); }
    }
    dlg.dispatchEvent(new CustomEvent("ioc:sheet-close", { bubbles: true, detail: { id: dlg.id } }));
  }
  function closeSheet(id, restoreFocus) {
    var dlg = sheetById(id);
    if (!dlg) return;
    dlg.__iocRestore = restoreFocus;
    if (dlg.open) dlg.close(); // fires "close", cleanup runs there
    else if (openSheets.indexOf(dlg) !== -1) cleanupSheet(dlg, restoreFocus);
  }
  // "close" does not bubble: capture it for every sheet however it closed (button, Escape on the modal path, JS).
  document.addEventListener("close", function (e) {
    var d = e.target;
    if (d && d.matches && d.matches("[data-ioc-sheet]") && openSheets.indexOf(d) !== -1) {
      var r = d.__iocRestore; d.__iocRestore = undefined;
      cleanupSheet(d, r);
    }
  }, true);
  document.addEventListener("click", function (e) {
    if (!e.target.closest) return;
    var opener = e.target.closest("[data-ioc-sheet-open]");
    if (opener) {
      if (openSheet(opener.getAttribute("data-ioc-sheet-open"), opener)) e.preventDefault();
      return;
    }
    var closer = e.target.closest("[data-ioc-sheet-close]");
    if (closer) { var d = closer.closest("[data-ioc-sheet]"); if (d) { e.preventDefault(); closeSheet(d); } return; }
    // a tap on the dim layer (the dialog itself, outside the panel) closes
    if (e.target.matches && e.target.matches("[data-ioc-sheet][open]")) { closeSheet(e.target); return; }
    // following a link inside a sheet (the menu) closes it, so a same-page #hash link is not left under the menu
    var link = e.target.closest("a[href]");
    var host = link && link.closest("[data-ioc-sheet][open]");
    if (host && !e.defaultPrevented) closeSheet(host, false);
  });
  // D2-B: every open sheet (the phone menu included) is a real modal for the keyboard. Tab and Shift+Tab are moved
  // by hand through the sheet's own controls and wrap at either end, so focus can never reach the page behind, the
  // browser chrome, or (in WebKit, which skips links on Tab by default) the next control outside the menu.
  function trapList(dlg) {
    return $$(FOCUSABLE, dlg).filter(function (el) {
      if (el.closest("[inert]") || el.closest("[hidden]")) return false;
      if (!el.getClientRects().length) return false;
      var cs = getComputedStyle(el);
      return cs.visibility !== "hidden" && cs.display !== "none";
    });
  }
  document.addEventListener("keydown", function (e) {
    if (!openSheets.length) return;
    var top = openSheets[openSheets.length - 1];
    if (e.key === "Escape") {
      if (top.__iocPop) { e.preventDefault(); closeSheet(top); } // the modal path closes natively ("cancel")
      return;
    }
    // Option+Tab is Safari's "every link" Tab on a Mac, so it is trapped too (Windows keeps Alt+Tab for itself)
    if (e.key !== "Tab" || e.ctrlKey || e.metaKey) return;
    // another dialog (the cart drawer, search) opened over the sheet owns the keyboard
    var other = document.querySelector("dialog[open]:not([data-ioc-sheet])");
    if (other && other !== top && !top.contains(other)) return;
    var list = trapList(top);
    e.preventDefault();
    if (!list.length) { try { top.focus({ preventScroll: true }); } catch (err) {} return; }
    var i = list.indexOf(document.activeElement);
    var n = i === -1 ? (e.shiftKey ? list.length - 1 : 0) : (i + (e.shiftKey ? -1 : 1) + list.length) % list.length;
    try { list[n].focus({ preventScroll: false }); } catch (err) { list[n].focus(); }
  });
  // a pointer or script moving focus outside an open sheet pulls it back in (the page behind is inert anyway)
  document.addEventListener("focusin", function (e) {
    if (!openSheets.length) return;
    var top = openSheets[openSheets.length - 1];
    if (!top.__iocPop || top.contains(e.target)) return;
    if (e.target.closest && e.target.closest("[id^='shopify-pc'], #ioc-cc, .shopify-pc__banner")) return; // the consent banner stays usable
    if (e.target.closest && e.target.closest("dialog[open]:not([data-ioc-sheet])")) return;
    var list = trapList(top);
    if (list.length) { try { list[0].focus({ preventScroll: true }); } catch (err) {} }
  });
  // images in a closed sheet are lazy (display:none never loads them); start fetching the moment a thumb or
  // pointer heads for an opener, so the menu tiles are there when it opens
  function warm(e) {
    var o = e.target.closest && e.target.closest("[data-ioc-sheet-open]");
    var d = o && sheetById(o.getAttribute("data-ioc-sheet-open"));
    if (!d || d.__iocWarm) return;
    d.__iocWarm = true;
    $$("img[loading='lazy']", d).forEach(function (img) { img.loading = "eager"; });
  }
  document.addEventListener("pointerdown", warm, { passive: true });
  document.addEventListener("pointerover", warm, { passive: true });
  document.addEventListener("focusin", warm);
  window.IOCSheet = {
    open: function (id, opener) { return openSheet(id, opener); },
    close: function (id) { closeSheet(id); },
    isOpen: function (id) { var d = sheetById(id); return !!(d && d.open); }
  };

  /* ================================================================ 4. desktop nav panels ===== */
  function initDropdowns() {
    var items = $$("[data-ioc-dd]");
    if (!items.length) return;
    var hover = window.matchMedia && window.matchMedia("(hover: hover) and (pointer: fine)").matches;
    function parts(item) {
      var btn = item.querySelector("[aria-controls]");
      return { btn: btn, panel: btn && document.getElementById(btn.getAttribute("aria-controls")) };
    }
    function set(item, open) {
      var p = parts(item);
      if (!p.btn || !p.panel) return;
      if (open && p.panel.hidden) item.__iocOpenedAt = Date.now();
      if (open) $$("img[loading='lazy']", p.panel).forEach(function (img) { img.loading = "eager"; });
      p.panel.hidden = !open;
      p.btn.setAttribute("aria-expanded", open ? "true" : "false");
      if (open) items.forEach(function (o) { if (o !== item) set(o, false); });
    }
    items.forEach(function (item) {
      var p = parts(item), t;
      if (!p.btn || !p.panel) return;
      item.addEventListener("pointerover", function () { $$("img[loading='lazy']", p.panel).forEach(function (img) { img.loading = "eager"; }); }, { once: true });
      p.btn.addEventListener("click", function () {
        // a hover just opened it: the click that follows should not snap it shut again
        if (!p.panel.hidden && Date.now() - (item.__iocOpenedAt || 0) < 600) return;
        set(item, p.panel.hidden);
      });
      if (hover) {
        item.addEventListener("mouseenter", function () { clearTimeout(t); t = setTimeout(function () { set(item, true); }, 70); });
        item.addEventListener("mouseleave", function () { clearTimeout(t); t = setTimeout(function () { set(item, false); }, 200); });
      }
      item.addEventListener("keydown", function (e) {
        if (e.key === "Escape" && !p.panel.hidden) { e.preventDefault(); set(item, false); p.btn.focus(); }
      });
      item.addEventListener("focusout", function (e) {
        if (e.relatedTarget && !item.contains(e.relatedTarget)) set(item, false);
      });
    });
    document.addEventListener("click", function (e) {
      items.forEach(function (item) { if (!item.contains(e.target)) set(item, false); });
    });
    /* Ship Gate a11y (WCAG 1.4.13): a panel that hover opened closes on Escape wherever focus is, without the shopper
       having to move the pointer or focus. Inside the item, the item's own handler above also returns focus. */
    document.addEventListener("keydown", function (e) {
      if (e.key !== "Escape") return;
      items.forEach(function (item) {
        var p = parts(item);
        if (p.panel && !p.panel.hidden && !item.contains(document.activeElement)) set(item, false);
      });
    });
  }

  /* ================================================================ 5. footer accordion ===== */
  function initAccordions() {
    var mq = window.matchMedia("(max-width: 899px)");
    $$("[data-ioc-acc]").forEach(function (acc) {
      if (acc.hasAttribute("data-ioc-acc-ready")) return;
      var cols = $$(".foot-col", acc);
      function apply() {
        cols.forEach(function (c) {
          var b = c.querySelector(".foot-col__btn");
          if (!b) return;
          if (mq.matches) { b.setAttribute("aria-expanded", c.classList.contains("is-open") ? "true" : "false"); b.removeAttribute("tabindex"); }
          else { b.setAttribute("aria-expanded", "true"); b.setAttribute("tabindex", "-1"); }
        });
      }
      cols.forEach(function (c) {
        var b = c.querySelector(".foot-col__btn");
        if (!b) return;
        b.addEventListener("click", function () {
          if (!mq.matches) return;
          var willOpen = !c.classList.contains("is-open");
          cols.forEach(function (o) { o.classList.remove("is-open"); });
          if (willOpen) c.classList.add("is-open");
          apply();
        });
      });
      acc.setAttribute("data-ioc-acc-ready", "");
      apply();
      if (mq.addEventListener) mq.addEventListener("change", apply);
    });
  }

  /* ================================================================ 6. RAIL ===== */
  var ARROW = {
    prev: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M20 12H6m6-6-6 6 6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>',
    next: '<svg viewBox="0 0 24 24" width="18" height="18" aria-hidden="true"><path d="M4 12h14m-6-6 6 6-6 6" fill="none" stroke="currentColor" stroke-width="2.4" stroke-linecap="round" stroke-linejoin="round"/></svg>'
  };
  function initRail(rail) {
    if (rail.__iocRail) return;
    var track = rail.querySelector(".ioc-rail__track");
    if (!track) return;
    rail.__iocRail = true;
    var cards = Array.prototype.filter.call(track.children, function (c) { return c.tagName === "LI" || c.classList.contains("ioc-rail__card"); });
    var n = cards.length;
    var label = rail.getAttribute("aria-label") || track.getAttribute("data-ioc-rail-label") || "Carousel";
    if (!rail.getAttribute("role")) rail.setAttribute("role", "region");
    rail.setAttribute("aria-roledescription", "carousel");
    if (!rail.getAttribute("aria-label")) rail.setAttribute("aria-label", label);
    if (!track.hasAttribute("data-ioc-rail")) track.setAttribute("data-ioc-rail", "");
    cards.forEach(function (c, i) {
      c.classList.add("ioc-rail__card");
      c.setAttribute("aria-roledescription", "slide");
      c.setAttribute("aria-label", (i + 1) + " of " + n);
    });
    var ui = rail.querySelector(".ioc-rail__ui");
    if (!ui) { ui = document.createElement("div"); ui.className = "ioc-rail__ui"; track.insertAdjacentElement("afterend", ui); }
    var dots = null;
    if (n > 1 && n <= 5) {
      dots = document.createElement("div");
      dots.className = "ioc-rail__dots";
      dots.setAttribute("aria-hidden", "true");
      for (var d = 0; d < n; d++) { var s = document.createElement("span"); s.className = "ioc-rail__dot"; dots.appendChild(s); }
      ui.appendChild(dots);
    }
    var count = null;
    if (n > 5) {
      count = document.createElement("span");
      count.className = "ioc-rail__count";
      count.setAttribute("aria-hidden", "true");
      ui.appendChild(count);
    }
    function mkArrow(dir) {
      var b = document.createElement("button");
      b.type = "button";
      b.className = "ioc-rail__arrow ioc-rail__arrow--" + dir;
      b.setAttribute("aria-label", (dir === "prev" ? "Previous" : "Next") + ": " + label);
      b.innerHTML = ARROW[dir];
      b.addEventListener("click", function () {
        var step = cards[1] ? Math.abs(cards[1].getBoundingClientRect().left - cards[0].getBoundingClientRect().left) : track.clientWidth * 0.8;
        track.scrollBy({ left: (dir === "prev" ? -1 : 1) * step, behavior: reduced() ? "auto" : "smooth" });
      });
      ui.appendChild(b);
      return b;
    }
    var prev = mkArrow("prev"), next = mkArrow("next");
    function current() {
      var max = track.scrollWidth - track.clientWidth;
      if (max <= 2) return 0;
      if (track.scrollLeft >= max - 2) return n - 1;
      var edge = track.getBoundingClientRect().left + (parseFloat(getComputedStyle(track).paddingLeft) || 0);
      var best = 0, bestD = Infinity;
      cards.forEach(function (c, i) { var dd = Math.abs(c.getBoundingClientRect().left - edge); if (dd < bestD) { bestD = dd; best = i; } });
      return best;
    }
    function update() {
      var scrollable = track.scrollWidth > track.clientWidth + 2;
      ui.hidden = n < 2 || !scrollable;
      var i = current();
      if (count) count.textContent = (i + 1) + " / " + n;
      if (dots) Array.prototype.forEach.call(dots.children, function (el, k) { el.classList.toggle("is-on", k === i); });
      var atStart = track.scrollLeft <= 2, atEnd = track.scrollLeft >= track.scrollWidth - track.clientWidth - 2;
      // final-a (WCAG 2.4.3): disabling the focused arrow would drop focus to the page body; hand it to the other arrow first
      if (atEnd && !atStart && document.activeElement === next) prev.focus({ preventScroll: true });
      if (atStart && !atEnd && document.activeElement === prev) next.focus({ preventScroll: true });
      prev.disabled = atStart;
      next.disabled = atEnd;
    }
    track.addEventListener("scroll", raf(update), { passive: true });
    window.addEventListener("resize", raf(update));
    update();
  }
  function initRails() { $$(".ioc-rail").forEach(initRail); }

  /* ================================================================ 7. TABS ===== */
  function initTabs() {
    $$("[data-ioc-tabs]").forEach(function (list) {
      if (list.__iocTabs) return;
      list.__iocTabs = true;
      var gid = list.getAttribute("data-ioc-tabs");
      var tabs = $$("[role='tab']", list);
      if (!tabs.length) return;
      var def = tabs[0].getAttribute("data-ioc-tab");
      function select(key, opts) {
        opts = opts || {};
        var hit = null;
        tabs.forEach(function (t) {
          var on = t.getAttribute("data-ioc-tab") === key;
          if (on) hit = t;
          t.setAttribute("aria-selected", on ? "true" : "false");
          t.tabIndex = on ? 0 : -1;
          var p = document.getElementById(t.getAttribute("aria-controls"));
          if (p) p.hidden = !on;
        });
        if (!hit) return;
        if (opts.hash) {
          var url = location.pathname + location.search + (key === def ? "" : "#" + key);
          try { history.replaceState(history.state, "", url); } catch (e) {}
        }
        if (opts.focus) hit.focus();
        list.dispatchEvent(new CustomEvent("ioc:tab", { bubbles: true, detail: { id: gid, key: key } }));
      }
      tabs.forEach(function (t, i) {
        t.addEventListener("click", function () { select(t.getAttribute("data-ioc-tab"), { hash: true }); });
        t.addEventListener("keydown", function (e) {
          var j = null;
          if (e.key === "ArrowRight") j = (i + 1) % tabs.length;
          else if (e.key === "ArrowLeft") j = (i - 1 + tabs.length) % tabs.length;
          else if (e.key === "Home") j = 0;
          else if (e.key === "End") j = tabs.length - 1;
          if (j === null) return;
          e.preventDefault();
          select(tabs[j].getAttribute("data-ioc-tab"), { hash: true, focus: true });
        });
      });
      function fromHash() {
        var h = "";
        try { h = decodeURIComponent(location.hash.slice(1)); } catch (e) {}
        if (h && tabs.some(function (t) { return t.getAttribute("data-ioc-tab") === h; })) select(h);
        else if (!h) select(def);
      }
      fromHash();
      window.addEventListener("hashchange", fromHash);
    });
  }

  /* ================================================================ 8. ACTION-BAR ===== */
  function money(cents) {
    var cur = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || "USD";
    try { return new Intl.NumberFormat(root.lang || "en", { style: "currency", currency: cur }).format(cents / 100); }
    catch (e) { return "$" + (cents / 100).toFixed(2); }
  }
  function firstUsable(sel) {
    var list = [];
    try { list = $$(sel); } catch (e) {}
    for (var i = 0; i < list.length; i++) if (!list[i].disabled && list[i].getClientRects().length) return list[i];
    return list[0] || null;
  }
  function initActionBar() {
    var bar = document.querySelector("[data-ioc-abar]");
    if (!bar) return;
    var mode = bar.getAttribute("data-mode");
    var show = bar.getAttribute("data-show") || "after";
    var go = bar.querySelector("[data-ioc-abar-go]");
    var labelEl = bar.querySelector("[data-ioc-abar-label]");
    if (!go || !labelEl) return;
    var defLabel = labelEl.textContent;
    var defHref = go.getAttribute("href");
    var active = null; // the card driving the bar, if any
    var watched = [];
    // subscribe page: once a plan card has driven the bar it keeps driving it when no card is on screen (D1-B)
    var keepCard = bar.hasAttribute("data-keep-card");

    function collect() {
      function usable(el) { return !bar.contains(el) && el.getClientRects().length > 0; }
      // Marked CTAs are watched even while hidden (the other size pane of a pack card, a closed tab): evaluate()
      // skips whatever has no box at that moment, so a pane that appears later is already being watched.
      var marked = $$("[data-ioc-primary-cta]").filter(function (el) { return !bar.contains(el); });
      // fix wave 2026-09-24: the newsletter signup form (ioc-catch) yields the bar wherever it sits on a page, the
      // same way a contact-form page's own form always does below (data-watch-also); merged into "marked" so both
      // branches below see it.
      $$("[data-ioc-abar-yield]").forEach(function (el) {
        if (!bar.contains(el) && marked.indexOf(el) === -1) marked.push(el);
      });
      var sel = bar.getAttribute("data-watch");
      // D2-B (P1-5): on the form pages the form itself is always watched as well (both GROUP-FORM steps sit inside
      // it), so "Tell us about it" never sits on screen next to the form's own Next or Send.
      if (bar.hasAttribute("data-watch-also") && sel) {
        var extra = [];
        try { extra = $$(sel).filter(function (el) { return !bar.contains(el); }); } catch (e) {}
        return marked.concat(extra.filter(function (el) { return marked.indexOf(el) === -1; }));
      }
      if (marked.some(usable)) return marked;
      if (!sel) return [];
      var found = [];
      try { found = $$(sel).filter(usable); } catch (e) { return []; }
      // the generic fallback ("the first orange button in main") watches only the first one: the page's hero CTA
      return bar.hasAttribute("data-watch-first") ? found.slice(0, 1) : found;
    }
    function headerBottom() { return parseFloat(getComputedStyle(root).getPropertyValue("--ioc-hdr-visible")) || 0; }

    // proxy modes need a real target; a dead button is worse than no bar
    var proxySel = go.getAttribute("data-ioc-abar-click");
    if (proxySel && !firstUsable(proxySel)) { bar.hidden = true; return; }

    // How much of a card is actually visible sideways: clipped by the viewport and by its nearest horizontal
    // scroller (a RAIL track), so in a sideways rail only the card the shopper is looking at counts.
    function sideways(c, r) {
      var left = Math.max(r.left, 0), right = Math.min(r.right, window.innerWidth);
      for (var p = c.parentElement; p && p !== document.body; p = p.parentElement) {
        if (/(auto|scroll)/.test(getComputedStyle(p).overflowX)) {
          var pr = p.getBoundingClientRect();
          left = Math.max(left, pr.left); right = Math.min(right, pr.right);
          break;
        }
      }
      return r.width ? Math.max(0, right - left) / r.width : 0;
    }
    function pickCard() {
      var cards = $$("[data-ioc-abar-card]");
      if (!cards.length) return null;
      var mid = window.innerHeight / 2, best = null, bestD = Infinity, bestV = 0;
      cards.forEach(function (c) {
        var r = c.getBoundingClientRect();
        if (!r.height || r.bottom < 0 || r.top > window.innerHeight) return;
        var v = sideways(c, r);
        if (v < 0.4) return; // a peeking neighbour in a rail, or swiped away
        var d = Math.abs((r.top + r.bottom) / 2 - mid);
        // cards on one rail row share a centre: the one most in view wins
        if (best && Math.abs(d - bestD) < 4) { if (v > bestV) { best = c; bestD = d; bestV = v; } return; }
        if (d < bestD) { bestD = d; best = c; bestV = v; }
      });
      return best;
    }
    function applyCard(c) {
      active = c;
      // re-read every time: a card's label or link can change in place (6 | 12 switch, Regular / Gluten Friendly)
      var lab = c ? (c.getAttribute("data-ioc-abar-label") || defLabel) : defLabel;
      var href = c ? (c.getAttribute("data-ioc-abar-href") || defHref || "#") : defHref;
      if (labelEl.textContent !== lab) labelEl.textContent = lab;
      if (go.tagName === "A" && href && go.getAttribute("href") !== href) go.setAttribute("href", href);
    }

    function evaluate() {
      if (!watched.length || watched.some(function (el) { return !el.isConnected; })) watched = collect();
      var top = headerBottom(), bottom = window.innerHeight - (isPhone() ? 64 : 0);
      var anyOn = false, anyAbove = false, measured = 0, firstAt = Infinity, st = scrollTop();
      watched.forEach(function (el) {
        var r = el.getBoundingClientRect();
        if (!r.width && !r.height) return;
        measured++;
        firstAt = Math.min(firstAt, r.top + st);
        if (r.bottom > top && r.top < bottom) anyOn = true;
        else if (r.bottom <= top) anyAbove = true;
      });
      // D2-B (P2): a page whose only primary sits at the END (press, 404) still gets the bar once the shopper has
      // scrolled 60% of a screen; it steps aside again the moment that button comes on screen. On a page so short that
      // its first primary comes on screen before that 60% mark (press or the 404 on a tall phone) the bar would never
      // show at all, so there it is up from the top until that button arrives. The cart page is D2-A's and keeps its rule.
      var past = st > window.innerHeight * 0.6;
      var late = firstAt > window.innerHeight && firstAt - bottom < window.innerHeight * 0.6 && !document.querySelector('[data-template="cart"]');
      var want = measured ? (!anyOn && (show === "away" || anyAbove || past || late)) : past;
      if (mode === "cards" || $$("[data-ioc-abar-card]").length) {
        var c = pickCard();
        if (c || !(keepCard && active && active.isConnected)) applyCard(c);
        else applyCard(active);
      }
      if (want && twinOnScreen(top, bottom)) want = false;
      bar.classList.toggle("is-on", want);
    }
    // D2-B (P1-6): never two identical primaries on one screen. Whatever the page marks, if a button in the page that
    // says the same thing as the bar ("Ship it to me", "Build a box", "Get the 12") is on screen, the bar stays down.
    function keyOf(t) { return String(t || "").replace(/\s+/g, " ").trim().toUpperCase().split(" · ")[0]; }
    var TWIN_SEL = "main a.btn, main button.btn, main [data-ioc-primary-cta], main button[name='add']";
    // final-a: one orange button on screen at a time, the bar included. Any other button in the page filled with the
    // bar's own orange (the email sign-up, a route card, the 404 search) sends the bar down too, not only a twin.
    function twinOnScreen(top, bottom) {
      var k = keyOf(labelEl.textContent);
      var fill = getComputedStyle(go).backgroundColor;
      var list = $$(TWIN_SEL);
      for (var i = 0; i < list.length; i++) {
        var el = list[i];
        if (bar.contains(el)) continue;
        var twin = !!k && keyOf(el.innerText || el.textContent) === k;
        var r = el.getBoundingClientRect();
        if (!r.width || !r.height || r.bottom <= top || r.top >= bottom || r.right <= 0 || r.left >= window.innerWidth) continue;
        if (sideways(el, r) < 0.5) continue; // a peeking card in a rail does not count
        var cs = getComputedStyle(el);
        if (cs.visibility === "hidden" || Number(cs.opacity) < 0.05) continue;
        if (twin || cs.backgroundColor === fill) return true;
      }
      return false;
    }
    var ev = raf(evaluate);
    onPageScroll(ev);
    // a sideways swipe in a rail that holds bar cards re-picks the card (scroll does not bubble; capture sees it)
    document.addEventListener("scroll", function (e) {
      var t = e.target;
      if (t && t.nodeType === 1 && !t.classList.contains("page-wrapper") && t.querySelector("[data-ioc-abar-card]")) ev();
    }, { capture: true, passive: true });
    // pages that change a card in place (type toggle) ask for a re-read
    document.addEventListener("ioc:abar-refresh", ev);
    window.addEventListener("resize", function () { watched = []; ev(); });
    window.addEventListener("load", function () { watched = []; ev(); });
    setTimeout(function () { watched = []; ev(); }, 1200);
    evaluate();

    go.addEventListener("click", function (e) {
      var c = active;
      if (c && c.getAttribute("data-ioc-abar-submit")) {
        e.preventDefault();
        var f = document.getElementById(c.getAttribute("data-ioc-abar-submit"));
        if (f) { if (f.requestSubmit) f.requestSubmit(); else f.submit(); }
        return;
      }
      if (c && c.getAttribute("data-ioc-abar-click")) {
        e.preventDefault();
        var t = firstUsable(c.getAttribute("data-ioc-abar-click"));
        if (t) t.click();
        return;
      }
      if (mode === "lp") {
        // D2-B (Cora #6): the LP bar buys, it does not just scroll. It presses the page's own buy button, so the add
        // carries the buy box's Flavors line property and whichever plan the shopper picked there (one-time unless
        // they chose a schedule). Without that button (no Box product set) it falls back to the #buy jump.
        var lpBtn = firstUsable("main [data-ioc-lp-buy] button[name='add']");
        if (lpBtn && !lpBtn.disabled) { e.preventDefault(); lpBtn.click(); return; }
      }
      if (proxySel && mode === "box" && boxToBuilder()) {
        // The Box never reaches the cart without its flavors: while the product form carries no Flavors line
        // property, the bar opens the builder on the chosen size, type and plan instead of adding (D1-B).
        e.preventDefault();
        window.location.href = boxToBuilder();
        return;
      }
      if (proxySel) {
        e.preventDefault();
        var target = firstUsable(proxySel);
        if (target && !target.disabled) target.click();
        else if (target) target.scrollIntoView({ block: "center", behavior: reduced() ? "auto" : "smooth" });
        return;
      }
      var scrollSel = go.getAttribute("data-ioc-abar-scroll");
      if (scrollSel) {
        e.preventDefault();
        var form = firstUsable(scrollSel);
        if (!form) return;
        form.scrollIntoView({ block: "start", behavior: reduced() ? "auto" : "smooth" });
        if (!form.hasAttribute("tabindex")) form.setAttribute("tabindex", "-1");
        try { form.focus({ preventScroll: true }); } catch (err) {}
      }
    });

    // product modes: the label follows the chosen variant and selling plan (prices printed by Liquid)
    var vjson = document.querySelector("[data-ioc-abar-variants]");
    var variants = [];
    // The Box product form with no Flavors property: the builder URL for the chosen variant, else "" (add as usual)
    function boxToBuilder() {
      var form = document.querySelector('main form[data-type="add-to-cart-form"]');
      var builder = bar.getAttribute("data-builder");
      if (!form || !builder) return "";
      var fd = new FormData(form);
      if (String(fd.get("properties[Flavors]") || "").trim()) return "";
      var id = Number(fd.get("id")), v = null;
      for (var i = 0; i < variants.length; i++) if (variants[i].id === id) v = variants[i];
      var q = [];
      if (v && Number(v.n)) q.push("tier=" + Number(v.n));
      if (v && v.t && /gluten/i.test(v.t)) q.push("type=" + encodeURIComponent(v.t));
      if (fd.get("selling_plan")) q.push("plan=" + (v && v.plans && v.plans[fd.get("selling_plan")] && /2 weeks|two weeks/i.test(v.plans[fd.get("selling_plan")].name) ? "2weeks" : "month"));
      return builder + (q.length ? (builder.indexOf("?") > -1 ? "&" : "?") + q.join("&") : "");
    }
    if (vjson && (mode === "box" || mode === "product")) {
      try { variants = JSON.parse(vjson.textContent); } catch (e) {}
      var refresh = function () {
        var form = document.querySelector('main form[data-type="add-to-cart-form"]');
        if (!form) return;
        var fd = new FormData(form);
        var id = Number(fd.get("id"));
        var plan = fd.get("selling_plan");
        var v = null;
        for (var i = 0; i < variants.length; i++) if (variants[i].id === id) v = variants[i];
        if (!v) return;
        var txt;
        var build = mode === "box" && !!boxToBuilder();
        if (plan && v.plans && v.plans[plan]) {
          var every = (String(v.plans[plan].name || "").match(/every [^,.]+/i) || [""])[0].toLowerCase();
          txt = (mode === "box" ? (build ? "Build the " + v.n + " · " : "Start · ") : "Subscribe · ") + v.plans[plan].p + (every ? " " + every : "");
        } else {
          txt = mode === "box" ? (build ? "Build the " : "Add the ") + v.n + " · " + v.price : "Add to cart · " + v.price;
        }
        if (!v.available) txt = "Sold out";
        go.disabled = !v.available;
        defLabel = txt;
        if (!active) labelEl.textContent = txt;
      };
      var later = function () { setTimeout(refresh, 60); };
      document.addEventListener("change", later);
      document.addEventListener("variant:update", later);
      refresh();
    }

    // LP: the bar says exactly what the buy box button says ("Get the 12 · $68.94" / "Start monthly · $58.94"),
    // because pressing the bar presses that button
    if (mode === "lp") {
      var lpSync = function () {
        var b = firstUsable("main [data-ioc-lp-buy] button[name='add']");
        var t = b && (b.getAttribute("data-ioc-lp-label") || b.textContent || "").replace(/\s+/g, " ").trim();
        if (!t) return;
        defLabel = t;
        if (!active && labelEl.textContent !== t) labelEl.textContent = t;
      };
      document.addEventListener("change", function () { setTimeout(lpSync, 30); });
      document.addEventListener("ioc:lp-label", lpSync);
      lpSync();
    }

    // cart page: the total follows cart changes
    if (mode === "cart") {
      var onCart = function () {
        fetch((window.Shopify && window.Shopify.routes ? window.Shopify.routes.root : "/") + "cart.js", { headers: { Accept: "application/json" } })
          .then(function (r) { return r.json(); })
          .then(function (c) {
            if (!c || !c.item_count) { bar.hidden = true; return; }
            bar.hidden = false;
            defLabel = "Check out · " + money(c.total_price);
            labelEl.textContent = defLabel;
          }).catch(function () {});
      };
      document.addEventListener("shopify:cart:lines-update", function () { setTimeout(onCart, 400); });
      document.addEventListener("cart:update", function () { setTimeout(onCart, 400); });
    }
  }

  /* ================================================================ 9. page state ===== */
  var TEXT_FIELD = "input:not([type='checkbox']):not([type='radio']):not([type='button']):not([type='submit']):not([type='reset']):not([type='range']):not([type='hidden']):not([type='image']):not([type='color']):not([type='file']), textarea";
  function isField(el) { return !!(el && el.matches && el.matches(TEXT_FIELD)); }
  document.addEventListener("focusin", function (e) { if (isField(e.target)) root.classList.add("ioc-typing"); });
  document.addEventListener("focusout", function (e) {
    if (!isField(e.target)) return;
    setTimeout(function () { if (!isField(document.activeElement)) root.classList.remove("ioc-typing"); }, 80);
  });
  // D2-B (WCAG 3.3.1): when a theme form field fails the browser's check, the reason is also written as text under the
  // field (the browser's own bubble fades and is not read out everywhere), tied to it with aria-describedby and
  // aria-invalid. It clears as soon as the field is valid again. Radio and checkbox groups get one line per group.
  var fieldErrN = 0;
  function errAnchor(el) {
    if (el.type === "radio" || el.type === "checkbox") return el.closest("fieldset") || el.parentElement;
    return null;
  }
  function errGroup(el) {
    if ((el.type === "radio" || el.type === "checkbox") && el.name && el.form) return Array.prototype.filter.call(el.form.elements, function (x) { return x.name === el.name; });
    return [el];
  }
  function setDescribed(el, id, on) {
    var ids = (el.getAttribute("aria-describedby") || "").split(/\s+/).filter(function (x) { return x && x !== id; });
    if (on) ids.push(id);
    if (ids.length) el.setAttribute("aria-describedby", ids.join(" ")); else el.removeAttribute("aria-describedby");
    if (on) el.setAttribute("aria-invalid", "true"); else el.removeAttribute("aria-invalid");
  }
  document.addEventListener("invalid", function (e) {
    var el = e.target;
    // theme contact and sign-up forms only (they all post to /contact); product, cart and search forms are D2-A's
    if (!el || !el.closest || !el.closest(".ioc") || !el.form || String(el.form.getAttribute("action") || "").indexOf("/contact") < 0) return;
    var group = errGroup(el), first = group[0];
    if (!first.id) first.id = "ioc-f" + (++fieldErrN);
    var id = first.id + "-err", msg = document.getElementById(id);
    if (!msg) {
      msg = document.createElement("p");
      msg.id = id;
      msg.className = "ioc-field-err";
      var anchor = errAnchor(el);
      if (anchor) anchor.appendChild(msg);
      else {
        var p = el.parentElement, pcs = p && getComputedStyle(p);
        // a field sharing a no-wrap row with its button: the line goes under the whole row
        if (p && pcs.display.indexOf("flex") > -1 && pcs.flexWrap === "nowrap" && pcs.flexDirection.indexOf("row") === 0) p.insertAdjacentElement("afterend", msg);
        else el.insertAdjacentElement("afterend", msg);
      }
    }
    msg.textContent = el.validationMessage || "Please fill in this field.";
    group.forEach(function (x) { setDescribed(x, id, true); });
  }, true);
  function clearFieldErr(e) {
    var el = e.target;
    if (!el || !el.getAttribute || el.getAttribute("aria-invalid") !== "true" || !el.checkValidity || !el.validity || !el.validity.valid) return;
    var group = errGroup(el), id = group[0].id + "-err", msg = document.getElementById(id);
    if (!msg || !msg.classList.contains("ioc-field-err")) return;
    msg.remove();
    group.forEach(function (x) { setDescribed(x, id, false); });
  }
  document.addEventListener("input", clearFieldErr, true);
  document.addEventListener("change", clearFieldErr, true);
  // html.ioc-past-fold once the page has scrolled 60% of a screen (the phone Rewards launcher waits for it, D1-B)
  // html.ioc-lolo-dodge while the Rewards launcher would sit on ANY control (D24, widened in D2-B): buy buttons, the
  // dock and the action bar, but also every link, field, toggle, tab and footer accordion, the email field, the social
  // links and in-card buttons. It steps aside (hidden, not faded) and comes back once nothing tappable is under it.
  // Swipe areas (a rail track, a scrolling table) are not controls and do not count; the controls inside them do.
  var LOLO_HIT = "a[href], button, input:not([type='hidden']), select, textarea, summary, label[for], [role='button'], [role='tab'], [role='link'], [role='checkbox'], [role='radio'], [role='switch'], [tabindex]:not([tabindex='-1']), [data-ioc-dock]";
  function isSwipeArea(el) {
    if (el.matches("a[href], button, input, select, textarea, summary")) return false;
    var cs = getComputedStyle(el);
    return (/(auto|scroll)/.test(cs.overflowX) && el.scrollWidth > el.clientWidth + 1) || (/(auto|scroll)/.test(cs.overflowY) && el.scrollHeight > el.clientHeight + 1);
  }
  function paints(el) {
    if (el.closest("[inert], dialog:not([open]), [hidden]")) return false;
    for (var e = el; e && e.nodeType === 1 && e !== document.body; e = e.parentElement) {
      var cs = getComputedStyle(e);
      if (cs.display === "none" || cs.visibility === "hidden" || Number(cs.opacity) < 0.05) return false;
    }
    return true;
  }
  function loloDodge() {
    var w = document.querySelector(".loloyal-launcher-wrapper");
    var r = w && w.getBoundingClientRect();
    if (!r || !r.width || !r.height) { root.classList.remove("ioc-lolo-dodge"); return; }
    var hit = false, list = document.querySelectorAll(LOLO_HIT), pad = 6;
    for (var i = 0; i < list.length && !hit; i++) {
      var el = list[i];
      if (w.contains(el) || el.contains(w)) continue;
      var q = el.getBoundingClientRect();
      if (!q.width || !q.height) continue;
      if (!(q.left < r.right + pad && q.right > r.left - pad && q.top < r.bottom + pad && q.bottom > r.top - pad)) continue;
      if (isSwipeArea(el) || !paints(el)) continue;
      hit = true;
    }
    root.classList.toggle("ioc-lolo-dodge", hit);
  }
  var foldSync = raf(function () { root.classList.toggle("ioc-past-fold", scrollTop() > window.innerHeight * 0.6); loloDodge(); });
  onPageScroll(foldSync);
  window.addEventListener("resize", foldSync);
  // a sideways swipe in a rail can slide a buy button under the launcher too
  document.addEventListener("scroll", function (e) { var t = e.target; if (t && t.nodeType === 1 && t.classList.contains("ioc-rail__track")) foldSync(); }, { capture: true, passive: true });
  window.addEventListener("load", function () { setTimeout(foldSync, 1500); setTimeout(foldSync, 4000); });
  // a tap can move controls without any scroll (a footer accordion opening, a tab, a GROUP-FORM step): re-check
  document.addEventListener("click", function () { setTimeout(foldSync, 60); setTimeout(foldSync, 320); }, true);
  document.addEventListener("toggle", function () { setTimeout(foldSync, 60); }, true);
  foldSync();
  function watchDialogs() {
    var sync = raf(function () {
      var other = document.querySelector("dialog[open]:not([data-ioc-sheet])");
      root.classList.toggle("ioc-dialog-open", !!other);
      // another dialog (the cart drawer after an add, search) takes over: step aside, without stealing its focus
      if (other && openSheets.length) openSheets.slice().forEach(function (d) { closeSheet(d, false); });
    });
    new MutationObserver(sync).observe(document.documentElement, { subtree: true, attributes: true, attributeFilter: ["open"] });
    sync();
  }

  /* final-a (WCAG 2.4.11): on phones the section snap (ioc-snap.css) pulled the page straight back to a section top
     after the browser scrolled a Tab stop into view, so in WebKit the flavor chips, the rating link and the subscribe
     FAQ links took focus below the screen. From the first Tab until the next touch or click, html.ioc-kbdnav switches
     the snap off (ioc-a11y.css). Touch scrolling keeps its snap. */
  document.addEventListener("keydown", function (e) {
    var k = e.key;
    if (k === "Tab" || k === "PageUp" || k === "PageDown" || k === "Home" || k === "End") { root.classList.add("ioc-kbdnav"); return; }
    if (k === " " || k === "Spacebar") {
      var t = e.target, tag = t && t.tagName ? t.tagName.toLowerCase() : "";
      var interactive = tag === "input" || tag === "textarea" || tag === "select" || tag === "button" || tag === "a" || (t && t.isContentEditable);
      if (!interactive) root.classList.add("ioc-kbdnav");
    }
  }, true);
  document.addEventListener("pointerdown", function () { root.classList.remove("ioc-kbdnav"); }, true);

  /* final-a (WCAG 2.4.11): WebKit ignores scroll-padding when the focused element is already inside the viewport, so a
     link sitting under Shopify's consent banner stayed hidden there. While the banner is up (ioc-cro.js sets
     html.ioc-consent-open and --ioc-consent-h), lift the focused element clear of it. In Chrome scroll-padding usually
     does this already and the check finds nothing to move; it only acts on what is still under the banner. Elements
     inside fixed or sticky layers are left alone. */
  /* Ship Gate 2026-09-30: the builder dock sits ON TOP of the banner (ioc-a11y.css lifts it by --ioc-consent-h), so the
     clear line is banner plus dock. With the banner up, Tab left the craft link, a rail arrow and a footer button fully
     behind the dock on the builder at 390. The dock counts even with no banner. */
  document.addEventListener("focusin", function (e) {
    var el = e.target;
    var consent = root.classList.contains("ioc-consent-open"), dock = root.classList.contains("ioc-has-dock");
    if ((!consent && !dock) || !el || el.nodeType !== 1 || el.closest("#shopify-pc__banner")) return;
    requestAnimationFrame(function () {
      if (document.activeElement !== el) return;
      var rs = getComputedStyle(root);
      var bh = (consent ? parseFloat(rs.getPropertyValue("--ioc-consent-h")) || 0 : 0) + (dock ? parseFloat(rs.getPropertyValue("--ioc-dock-h")) || 0 : 0);
      var r = el.getBoundingClientRect();
      var limit = window.innerHeight - bh - 12;
      if (!bh || !r.height || r.bottom <= limit || r.top < 0) return;
      for (var n = el; n && n !== document.body; n = n.parentElement) {
        var pos = getComputedStyle(n).position;
        if (pos === "fixed" || pos === "sticky") return;
      }
      var pw = document.querySelector(".page-wrapper");
      var sc = pw && pw.scrollHeight > pw.clientHeight + 1 && /auto|scroll/.test(getComputedStyle(pw).overflowY) ? pw : window;
      var head = (parseFloat(getComputedStyle(root).getPropertyValue("--ioc-hdr-visible")) || 0) + 8;
      sc.scrollBy({ top: Math.max(0, Math.min(r.bottom - limit, r.top - head)), behavior: "instant" });
    });
  });

  /* ================================================================ boot ===== */
  ready(function () {
    initHeader();
    initTicker();
    initDropdowns();
    initAccordions();
    initRails();
    initTabs();
    initActionBar();
    watchDialogs();
    // sections render lazily and the theme editor re-renders them: pick up new rails, tabs, accordions, tickers
    var t;
    new MutationObserver(function () {
      clearTimeout(t);
      t = setTimeout(function () { initRails(); initTabs(); initAccordions(); initTicker(); }, 200);
    }).observe(document.body, { childList: true, subtree: true });
    document.addEventListener("shopify:section:load", function () { initRails(); initTabs(); initAccordions(); initTicker(); initDropdowns(); });
  });
})();
